import { useState, type FormEvent } from 'react';
import { Skeleton } from '../components/Skeleton';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert } from '../components/Alert';
import { AppShell } from '../components/AppShell';
import { Button } from '../components/Button';
import { Dialog } from '../components/Dialog';
import { Field } from '../components/Field';
import { AdminReports } from '../components/admin/AdminReports';
import { api, queryKeys } from '../lib/api';
import { messageForError } from '../lib/errors';
import { relativeTime, shortDate } from '../lib/format';

export function AdminPage() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [includeDisabled, setIncludeDisabled] = useState(true);
  const [disabling, setDisabling] = useState<{ id: string; name: string } | null>(null);
  const [auditFor, setAuditFor] = useState<{ id: string; name: string } | null>(null);

  const list = useQuery({
    queryKey: queryKeys.adminHouseholds(q, includeDisabled),
    queryFn: () => api.admin.households.list({ ...(q ? { q } : {}), includeDisabled, limit: 100 }),
  });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin'] });
  const disable = useMutation({
    mutationFn: (v: { id: string; reason: string }) =>
      api.admin.households.disable(v.id, { reason: v.reason }),
    onSuccess: () => {
      setDisabling(null);
      invalidate();
    },
  });
  const enable = useMutation({
    mutationFn: (id: string) => api.admin.households.enable(id),
    onSuccess: invalidate,
  });
  const audit = useQuery({
    queryKey: queryKeys.adminAudit(auditFor?.id ?? null),
    queryFn: () => (auditFor ? api.admin.households.audit(auditFor.id) : api.admin.audit()),
    enabled: auditFor !== null,
  });

  function onDisable(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!disabling) return;
    const reason = String(new FormData(e.currentTarget).get('reason') ?? '').trim();
    if (reason) disable.mutate({ id: disabling.id, reason });
  }

  return (
    <AppShell title="Administration">
      <AdminReports />
      <h2 className="mb-2 text-lg font-bold">Households</h2>
      <p className="mb-3 text-sm text-muted">
        Disabling a household immediately removes its shared prices, locations, photos and public
        recipes from everyone else’s view. Its private data is kept.
      </p>
      <form
        className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-end"
        onSubmit={(e) => e.preventDefault()}
      >
        <div className="flex-1">
          <Field
            id="q"
            name="q"
            label="Search households"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Name"
          />
        </div>
        <label className="flex min-h-11 items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={includeDisabled}
            onChange={(e) => setIncludeDisabled(e.target.checked)}
          />
          Include disabled
        </label>
      </form>
      {list.isError ? <Alert tone="error">{messageForError(list.error)}</Alert> : null}
      {enable.isError ? <Alert tone="error">{messageForError(enable.error)}</Alert> : null}
      <div className="overflow-x-auto card">
        <table className="w-full text-sm">
          <thead className="bg-cream text-left text-xs uppercase text-muted">
            <tr>
              <th className="px-3 py-2">Household</th>
              <th className="px-3 py-2">Members</th>
              <th className="px-3 py-2">Created</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {list.data?.households.map((h) => (
              <tr key={h.id} className="border-t border-line">
                <td className="px-3 py-2 font-medium">{h.name}</td>
                <td className="px-3 py-2">{h.memberCount}</td>
                <td className="px-3 py-2">{shortDate(h.createdAt)}</td>
                <td className="px-3 py-2">
                  {h.disabledAt ? (
                    <span className="text-fail" title={h.disabledReason ?? ''}>
                      Disabled {relativeTime(h.disabledAt)}
                    </span>
                  ) : (
                    <span className="text-ok">Active</span>
                  )}
                </td>
                <td className="px-3 py-2">
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => setAuditFor({ id: h.id, name: h.name })}
                      className="min-h-9 rounded-lg border border-line px-2"
                    >
                      Audit
                    </button>
                    {h.disabledAt ? (
                      <button
                        type="button"
                        onClick={() => enable.mutate(h.id)}
                        className="min-h-9 rounded-lg border border-line px-2 text-ok"
                      >
                        Enable
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setDisabling({ id: h.id, name: h.name })}
                        className="min-h-9 rounded-lg border border-line px-2 text-fail"
                      >
                        Disable
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {list.data && list.data.households.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-3 py-4 text-center text-muted">
                  No households match.
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      {list.data ? <p className="mt-2 text-xs text-muted">{list.data.total} total</p> : null}

      <Dialog
        open={disabling !== null}
        onClose={() => setDisabling(null)}
        title={`Disable ${disabling?.name ?? ''}?`}
      >
        <form onSubmit={onDisable} className="flex flex-col gap-3">
          <Field
            id="reason"
            name="reason"
            label="Reason (recorded in the audit log)"
            required
            maxLength={500}
          />
          {disable.isError ? <Alert tone="error">{messageForError(disable.error)}</Alert> : null}
          <div className="grid grid-cols-2 gap-2">
            <Button type="button" variant="secondary" onClick={() => setDisabling(null)}>
              Cancel
            </Button>
            <Button type="submit" variant="danger" disabled={disable.isPending}>
              Disable
            </Button>
          </div>
        </form>
      </Dialog>

      <Dialog
        open={auditFor !== null}
        onClose={() => setAuditFor(null)}
        title={`Audit log — ${auditFor?.name ?? ''}`}
      >
        {audit.isPending ? <Skeleton lines={3} /> : null}
        {audit.isError ? <Alert tone="error">{messageForError(audit.error)}</Alert> : null}
        <ul className="max-h-[60vh] overflow-y-auto text-sm">
          {audit.data?.entries.map((e) => (
            <li key={e.id} className="border-t border-line py-2 first:border-0">
              <p className="font-medium">
                {e.action} <span className="text-muted">· {e.entity}</span>
              </p>
              <p className="text-xs text-muted">{new Date(e.at).toLocaleString()}</p>
              {e.detail !== undefined ? (
                <pre className="mt-1 overflow-x-auto rounded bg-cream p-2 text-xs">
                  {JSON.stringify(e.detail, null, 1)}
                </pre>
              ) : null}
            </li>
          ))}
          {audit.data && audit.data.entries.length === 0 ? (
            <li className="text-muted">Nothing recorded yet.</li>
          ) : null}
        </ul>
        <Button variant="secondary" onClick={() => setAuditFor(null)}>
          Close
        </Button>
      </Dialog>
    </AppShell>
  );
}
